/** TypeScript mirror of supabase/migrations/0001_init.sql (the subset the UI uses). */

export type MemberRole = 'owner' | 'manager' | 'staff' | 'viewer'
export type VatScheme = 'standard' | 'small_business' | 'none'
export type UnitKind = 'room' | 'apartment' | 'house' | 'pitch' | 'other'
export type BookingKind = 'booking' | 'block'
export type BookingStatus =
  | 'enquiry' | 'quoted' | 'option' | 'confirmed'
  | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show'
export type BookingChannel =
  | 'direct' | 'website' | 'phone' | 'email'
  | 'booking_com' | 'airbnb' | 'vrbo' | 'other'
export type ItemKind = 'accommodation' | 'fee' | 'extra' | 'discount'
export type FeeBasis = 'per_stay' | 'per_night' | 'per_person' | 'per_person_night' | 'percent'
export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired' | 'withdrawn'
export type InvoiceType = 'proforma' | 'deposit' | 'invoice' | 'credit_note'
export type InvoiceStatus = 'draft' | 'sent' | 'partly_paid' | 'paid' | 'overdue' | 'cancelled'
export type PaymentKind =
  | 'prepayment' | 'balance' | 'security_deposit' | 'security_deposit_refund' | 'refund'
export type PaymentMethod = 'bank_transfer' | 'cash' | 'card' | 'stripe' | 'paypal' | 'other'
export type DepositStatus =
  | 'not_required' | 'due' | 'held' | 'partially_retained' | 'retained' | 'released'

/** Statuses that occupy the unit. Enquiries and quotes deliberately do not. */
export const BLOCKING_STATUSES: BookingStatus[] = [
  'option', 'confirmed', 'checked_in', 'checked_out',
]

/** ISO date string, `YYYY-MM-DD`. Always a calendar date, never a timestamp. */
export type ISODate = string

export interface Tenant {
  id: string
  slug: string
  /**
   * Kundennummer bei StaySuite, vergeben bei der Anlage und danach unveränderlich.
   * Steht im Konto-Menü, damit der Support etwas hat, das der Vermieter vorlesen
   * kann. Kein Geheimnis und keine Berechtigung.
   */
  customer_number?: string
  name: string
  country: string
  currency: string
  locale: string
  timezone: string
  vat_scheme: VatScheme
  vat_rate_accommodation: number
  vat_rate_services: number
  /**
   * True when the rates, fees and levies entered in this tenant already contain
   * VAT — the way a holiday let advertises a nightly price. See `priceStay`.
   */
  prices_include_vat?: boolean
  legal_name?: string
  street?: string
  postcode?: string
  city?: string
  /**
   * The three parts joined, kept in step by a database trigger. Documents and guest
   * mail read this; the settings screen writes the parts. Do not write it directly.
   */
  address?: string
  vat_id?: string
  /**
   * Österreichische Steuernummer (Abgabenkontonummer), normalisiert auf neun
   * Ziffern — `lib/steuernummer.ts` normalisiert und prüft, das DB-Netz darunter
   * ist `tenants_tax_number_chk` (Vorgang 018). Führende Nullen sind bedeutsam,
   * deshalb string und nie eine Zahl. Neben `vat_id` die zweite Nummer, von der
   * die RKSV-Bestellstrecke eine verlangt.
   */
  tax_number?: string | null
  /**
   * Firmenbuch- bzw. Handelsregisternummer und das zugehörige Gericht.
   *
   * Pflichtangabe in geschäftlichen E-Mails eingetragener Unternehmen (§ 14 UGB in
   * Österreich, § 35a GmbHG in Deutschland) — nicht nur im Impressum, sondern in
   * jeder Mail, die aus dem Betrieb hinausgeht. Leer bei nicht eingetragenen
   * Einzelunternehmern; das ist kein fehlender Wert, sondern die Wahrheit.
   */
  company_register?: string
  company_register_court?: string
  email?: string
  phone?: string
  bank_holder?: string
  bank_iban?: string
  bank_bic?: string
  /**
   * Anzahlungsregel: Anteil am Gesamtpreis oder immer derselbe Betrag. Vorbelegung
   * für neue Buchungen — in der einzelnen Buchung bleibt der Betrag überschreibbar.
   */
  prepayment_mode?: 'percent' | 'fixed' | 'none'
  prepayment_percent?: number
  prepayment_fixed?: number
  /**
   * Kinderermäßigung auf den Zusatzpersonenpreis, nach Alter gestaffelt.
   * `upToAge` ist ausschließlich: 6 heißt „bis einschließlich 5 Jahre".
   */
  child_rates?: { upToAge: number; percent: number }[]
  /**
   * Worauf die Kinderermäßigung wirkt: nur auf den Aufpreis je Zusatzperson
   * (Ferienwohnung) oder auch auf den Übernachtungsanteil je Person (Pension).
   */
  child_rate_scope?: 'surcharge' | 'nightly'
  /** Ob der Hinweisstreifen mit Preisempfehlungen über dem Kalender erscheint. */
  price_advice_enabled?: boolean
  /**
   * Stornobedingungen des Betriebs — freiwillig. Leer heißt nicht „vergessen",
   * sondern „nichts vereinbart, es gelten die gesetzlichen Bestimmungen". Wird beim
   * Erzeugen eines Angebots nach `quotes.terms_text` kopiert; ab da zählt die Kopie.
   */
  cancellation_terms?: string | null
  /**
   * Die Ausstattungsmerkmale, die dieser Betrieb kennt — selbst benannt.
   * `units.amenities` kreuzt daraus an.
   */
  amenity_catalogue?: string[]
  /**
   * Öffentlich lesbare URL des hochgeladenen Logos. Erscheint im Kopf jedes Belegs.
   * Öffentlich, weil der Gast den Beleg ohne Anmeldung öffnet — im Bild steckt
   * nichts, was nicht ohnehin auf dem Briefpapier stünde.
   */
  logo_url?: string | null
  accent_color?: string
  /** Public enquiry form. */
  enquiry_enabled?: boolean
  enquiry_default_open?: boolean
  enquiry_intro?: Record<string, string>
  enquiry_max_nights?: number | null
  enquiry_lead_days?: number
  /**
   * The landlord's OWN Stripe account (Connect Standard). Display only — the token
   * that can act on it lives in a table no user role can read.
   */
  stripe_account_id?: string | null
  stripe_charges_enabled?: boolean
  /** Guest mail goes out through the AUVISO sender but must look like the landlord. */
  mail_from_name?: string | null
  mail_reply_to?: string | null
  mail_signature?: string | null
}

export interface Property {
  id: string
  tenant_id: string
  name: string
  address?: string
  postcode?: string
  city?: string
  country?: string
  checkin_from: string
  checkout_by: string
  sort_order: number
  active: boolean
}

export interface Unit {
  id: string
  tenant_id: string
  property_id: string
  name: string
  code?: string
  kind: UnitKind
  max_occupancy: number
  base_occupancy: number
  bedrooms?: number
  size_sqm?: number
  category_id?: string | null
  base_price: number
  extra_person_price: number
  min_nights: number
  color: string
  description: Record<string, string>
  amenities: string[]
  sort_order: number
  active: boolean
}

export interface UnitCategory {
  id: string
  tenant_id: string
  name: string
  kind: UnitKind
  default_occupancy: number
  default_max_occupancy: number
  default_price: number
  default_min_nights: number
  color: string
  sort_order: number
  active: boolean
}

export interface RatePeriod {
  id: string
  tenant_id: string
  /** Scope, most specific first: unit_id > category_id > neither (all units). */
  unit_id: string | null
  category_id?: string | null
  name: string
  valid_from: ISODate
  valid_to: ISODate
  /** Bit 0 = Monday … bit 6 = Sunday. */
  dow_mask: number
  price_per_night: number
  extra_person_price: number | null
  base_occupancy: number | null
  min_nights: number | null
  priority: number
  active: boolean
  /** False = this season accepts no online enquiries (typically high season). */
  online_enquiry?: boolean
}

export interface Fee {
  id: string
  tenant_id: string
  unit_id: string | null
  code: string
  name: Record<string, string>
  basis: FeeBasis
  amount: number
  vat_rate: number
  mandatory: boolean
  taxable_age_from: number | null
  max_nights: number | null
  sort_order: number
  active: boolean
}

export interface Guest {
  id: string
  tenant_id: string
  first_name?: string
  last_name?: string
  company?: string
  email?: string
  phone?: string
  address?: string
  postcode?: string
  city?: string
  country?: string
  language: string
  notes?: string
  blacklisted: boolean
}

export interface BookingItem {
  id: string
  tenant_id: string
  booking_id: string
  kind: ItemKind
  code?: string
  label: string
  qty: number
  unit_price: number
  amount_net: number
  vat_rate: number
  amount_vat: number
  amount_gross: number
  sort_order: number
}

export interface Booking {
  id: string
  tenant_id: string
  unit_id: string
  guest_id: string | null
  reference: string
  kind: BookingKind
  status: BookingStatus
  channel: BookingChannel
  arrival: ISODate
  /** Exclusive: the guest leaves on this date, so it is free for the next arrival. */
  departure: ISODate
  adults: number
  children: number
  children_ages: number[]
  /** Non-mandatory fees the guest opted into (pet, linen, parking, …). */
  optional_fee_ids: string[]
  currency: string
  total_net: number
  total_vat: number
  total_gross: number
  prepayment_amount: number
  prepayment_due?: ISODate | null
  /** Checkout auf dem Stripe-Konto des Vermieters — siehe 0033. */
  stripe_prepay_session_id?: string | null
  stripe_prepay_url?: string | null
  prepaid_at?: string | null
  deposit_amount: number
  deposit_status: DepositStatus
  external_ref?: string
  notes?: string
  internal_notes?: string
  cancelled_at?: string | null
  cancel_reason?: string
  /**
   * Last day an `option` hold is valid. A nightly job releases the unit the day
   * after, by moving the booking back to `enquiry` — the guest still asked, and an
   * enquiry does not block.
   */
  option_until?: string | null
  option_expired_at?: string | null
  /**
   * Ein für diesen Aufenthalt vereinbarter Nachtpreis statt des Preises aus der
   * Liste. Gilt für jede Nacht; Ortstaxe, Nebenkosten und Zusatzpersonen bleiben
   * unberührt. Anders als `price_locked` friert er nichts ein — der Gesamtpreis wird
   * weiter neu gerechnet, wenn sich Zeitraum oder Belegung ändern.
   */
  nightly_rate_override?: number | null
  /** Nachlass auf den Aufenthalt, in der Währung der Buchung. */
  discount_amount?: number
  /** Warum es ihn gibt („Gutschein", „Stammgast 10 %"). Steht so auf der Rechnung. */
  discount_reason?: string | null
  /** Keep the stored total even when the dates or unit would price differently. */
  price_locked?: boolean
  price_locked_note?: string | null
  moved_at?: string | null
  /** A confirmation later than `moved_at` is stale and the guest needs a new one. */
  confirmation_sent_at?: string | null
  created_at: string
}

export interface Quote {
  id: string
  tenant_id: string
  booking_id: string
  number: string
  status: QuoteStatus
  language: string
  valid_until?: ISODate
  currency: string
  total_net: number
  total_vat: number
  total_gross: number
  prepayment_amount: number
  deposit_amount: number
  intro_text?: string
  terms_text?: string
  public_token: string
  sent_at?: string | null
  decided_at?: string | null
  /**
   * Wann der Gast das Angebot über seinen Link zum ersten Mal geöffnet hat.
   * Gesetzt von `public_document_seen`, danach unverändert — es ist die Frage
   * „hat er es überhaupt gesehen", nicht „wie oft".
   */
  viewed_at?: string | null
  /** Vom Gast selbst über die öffentliche Seite entschieden (Migration 0044). */
  accepted_at?: string | null
  declined_at?: string | null
  decline_reason?: string | null
  /** Line items frozen at the moment the quote was created. */
  snapshot?: { lines?: unknown[] } | null
  created_at: string
}

export interface InvoiceItem {
  id: string
  invoice_id: string
  kind: ItemKind
  /**
   * Herkunft der Zeile aus der Preisrechnung (`accommodation`, `tourist_tax`, …).
   * Fehlt bei Rechnungen, die vor Migration 0040 entstanden sind.
   */
  code?: string | null
  label: string
  qty: number
  unit_price: number
  amount_net: number
  vat_rate: number
  amount_vat: number
  amount_gross: number
  sort_order: number
}

export interface Invoice {
  id: string
  tenant_id: string
  booking_id: string | null
  guest_id: string | null
  /**
   * NULL while the invoice is a draft. A number is taken only when it is issued —
   * burning one on a draft that is later deleted leaves a gap in a sequence that
   * has to be gapless.
   */
  number: string | null
  type: InvoiceType
  status: InvoiceStatus
  language: string
  issue_date: ISODate
  service_from?: ISODate
  service_to?: ISODate
  due_date?: ISODate
  currency: string
  total_net: number
  total_vat: number
  total_gross: number
  paid_amount: number
  vat_note?: string
  public_token: string
  /** Set when the invoice was issued. Non-null means the row is immutable. */
  issued_at?: string | null
  /** On a credit note: the invoice it reverses. The Storno link, both ways. */
  corrects_invoice_id?: string | null
  /** Free text above the lines. On a credit note this carries the Storno reason. */
  intro_text?: string | null
  sent_at?: string | null
  paid_at?: string | null
  created_at: string
}

export interface Payment {
  id: string
  tenant_id: string
  booking_id: string | null
  invoice_id: string | null
  kind: PaymentKind
  method: PaymentMethod
  amount: number
  currency: string
  received_on: ISODate
  reference?: string
  note?: string
  created_at: string
}

export interface IcalFeed {
  id: string
  tenant_id: string
  unit_id: string
  direction: 'import' | 'export'
  label: string
  url?: string
  token: string
  active: boolean
  last_sync_at?: string | null
  last_status?: string
  last_error?: string
  event_count: number
}

export interface IcalEvent {
  id: string
  tenant_id: string
  feed_id: string
  unit_id: string
  uid: string
  arrival: ISODate
  departure: ISODate
  summary?: string
  booking_id?: string | null
  dismissed: boolean
  /** Gesetzt, wenn die UID aus ihrem Feed verschwunden ist. Siehe 0032. */
  cancelled_at?: string | null
}

export interface Database {
  /**
   * Nur in der Demo gesetzt: der Tag, an dem die Beispieldaten erzeugt wurden. Alles
   * darin liegt relativ zu diesem Tag, deshalb ist ein Abzug von gestern morgen falsch.
   */
  seeded_on?: string
  tenant: Tenant
  properties: Property[]
  unit_categories: UnitCategory[]
  units: Unit[]
  rate_periods: RatePeriod[]
  fees: Fee[]
  guests: Guest[]
  bookings: Booking[]
  booking_items: BookingItem[]
  quotes: Quote[]
  invoices: Invoice[]
  invoice_items: InvoiceItem[]
  payments: Payment[]
  ical_feeds: IcalFeed[]
  ical_events: IcalEvent[]
}
